import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Close as CloseIcon,
  Group as GroupIcon,
  Add as AddIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatDropOverlay } from '@/components/chat/ChatDropOverlay'
import { ChatThread } from '@/components/chat/ChatThread'
import { LessonChatHistoryDialog } from '@/components/chat/LessonChatHistoryDialog'
import { useChatComposer } from '@/components/chat/useChatComposer'
import { useChatPresence } from '@/components/chat/useChatPresence'
import { useChatStore } from '@/stores/chatStore'
import { useAuthStore } from '@/stores/authStore'
import { chatApi, isLessonChatWidget } from '@/api/chat'
import type { ChatContact, ChatMessage, ChatRoom, LessonChatWidgetMeta } from '@/api/chat'
import { getErrorMessage } from '@/utils/error'
import { unlockChatSound } from '@/utils/chatSound'
import '@/styles/dashboard.css'

const MUTE_UNTIL_FAR_FUTURE = '2099-12-31T23:59:59.000Z'

/** Высота верхней строки панели: «Новый чат» слева = заголовок чата справа. */
const CHAT_TOOLBAR_ROW_SX = {
  px: 1.5,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const

export function ChatPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ userId?: string; groupId?: string; roomId?: string }>()
  const currentUser = useAuthStore((s) => s.user)
  const currentUserId = currentUser?.id ?? 0

  const {
    rooms,
    contacts,
    currentRoomId,
    isLoadingRooms,
    isLoadingContacts,
    error,
    fetchRooms,
    fetchContacts,
    openDirect,
    setCurrentRoom,
    deleteMessage,
    setRoomMuted,
    toggleReaction,
    clearError,
  } = useChatStore()

  const [deepLinkError, setDeepLinkError] = useState<string | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [newChatOpen, setNewChatOpen] = useState(false)
  const [isMuting, setIsMuting] = useState(false)
  const [roomSearchQuery, setRoomSearchQuery] = useState('')
  const [lessonHistoryMeta, setLessonHistoryMeta] = useState<LessonChatWidgetMeta | null>(null)

  useEffect(() => {
    void fetchRooms()
  }, [fetchRooms])

  useEffect(() => {
    if (!params.roomId) return
    const roomId = Number(params.roomId)
    if (!Number.isFinite(roomId)) return
    setCurrentRoom(roomId)
    navigate('/chat', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.roomId])

  useEffect(() => {
    if (!params.userId) return
    const userId = Number(params.userId)
    if (!Number.isFinite(userId)) return
    openDirect(userId)
      .then(() => navigate('/chat', { replace: true }))
      .catch((e) => setDeepLinkError(getErrorMessage(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.userId])

  useEffect(() => {
    if (!params.groupId) return
    const groupId = Number(params.groupId)
    if (!Number.isFinite(groupId)) return
    const room = rooms.find((r) => r.student_group_id === groupId)
    if (room) {
      setCurrentRoom(room.id)
      navigate('/chat', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.groupId, rooms])

  const currentRoom = useMemo(
    () => rooms.find((r) => r.id === currentRoomId) ?? null,
    [rooms, currentRoomId]
  )
  const canWrite = currentRoom?.can_write ?? false

  const composer = useChatComposer(currentRoomId, canWrite, setDeepLinkError)

  const filteredRooms = useMemo(
    () => filterRoomsBySearch(rooms, roomSearchQuery),
    [rooms, roomSearchQuery]
  )

  useChatPresence(currentRoomId)

  const handleSelectRoom = (roomId: number) => {
    setCurrentRoom(roomId)
  }

  const handleOpenNewChat = async () => {
    unlockChatSound()
    setNewChatOpen(true)
    if (contacts.length === 0) {
      await fetchContacts()
    }
  }

  const handleSelectContact = async (contact: ChatContact) => {
    setNewChatOpen(false)
    setDeepLinkError(null)
    try {
      if (contact.existing_room_id) {
        setCurrentRoom(contact.existing_room_id)
      } else {
        await openDirect(contact.user.id)
      }
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  const handleToggleMute = async () => {
    if (!currentRoomId || !currentRoom) return
    setIsMuting(true)
    try {
      const nextMuted = !currentRoom.is_muted
      await chatApi.mute(currentRoomId, nextMuted ? MUTE_UNTIL_FAR_FUTURE : null)
      setRoomMuted(currentRoomId, nextMuted)
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    } finally {
      setIsMuting(false)
    }
  }

  /**
   * Копирование текста сообщения из контекстного меню ленты. Стабильная
   * ссылка обязательна: MessageBubble обёрнут в memo, новая функция на каждый
   * рендер перерисовывала бы всю ленту на каждый realtime-патч реакций.
   * navigator.clipboard недоступен вне защищённого контекста — тогда подсказка
   * вместо тихого молчания.
   */
  const handleCopyMessageText = useCallback(
    (message: ChatMessage) => {
      const value = message.text ?? ''
      if (!value.trim()) return
      if (typeof navigator.clipboard?.writeText !== 'function') {
        setDeepLinkError(t('chat.copyFailed'))
        return
      }
      // Копируем текст как есть: отступы важны для кода и разметки, лента
      // рисует их через whiteSpace: 'pre-wrap'.
      navigator.clipboard
        .writeText(value)
        .catch(() => setDeepLinkError(t('chat.copyFailed')))
    },
    [t]
  )

  // Стабильные колбэки — иначе memo на MessageBubble бесполезен и каждый
  // символ в композере перерисовывает всю ленту.
  const handleToggleReaction = useCallback(
    (messageId: number, emoji: string) => {
      if (!currentRoomId) return
      void toggleReaction(currentRoomId, messageId, emoji, currentUserId)
    },
    [currentRoomId, toggleReaction, currentUserId]
  )

  const handleDelete = useCallback(
    (message: ChatMessage) => {
      if (!currentRoomId) return
      deleteMessage(currentRoomId, message.id).catch((e: unknown) => {
        setDeepLinkError(getErrorMessage(e))
      })
    },
    [currentRoomId, deleteMessage]
  )

  const handleOpenLessonHistory = useCallback((meta: LessonChatWidgetMeta) => {
    setLessonHistoryMeta(meta)
  }, [])

  return (
    <DashboardLayout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('chat.title')}
        </Typography>
      </Box>

      {(error || deepLinkError) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setDeepLinkError(null)
            clearError()
          }}
        >
          {deepLinkError || error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 340px) 1fr' },
          gap: 2,
          height: { md: 'calc(100vh - 140px)' },
          width: '100%',
        }}
      >
        <Paper variant="outlined" sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={CHAT_TOOLBAR_ROW_SX}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => void handleOpenNewChat()}
              sx={{ height: 40, textTransform: 'none' }}
            >
              {t('chat.newChat')}
            </Button>
          </Box>

          <Box sx={{ px: 1.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('chat.searchRooms')}
              value={roomSearchQuery}
              onChange={(e) => setRoomSearchQuery(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: roomSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRoomSearchQuery('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {isLoadingRooms ? (
            <Box sx={{ p: 2 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={56} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : rooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noRooms')}</Typography>
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noSearchResults')}</Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
              {filteredRooms.map((room) => (
                <ListItemButton
                  key={room.id}
                  selected={room.id === currentRoomId}
                  onClick={() => handleSelectRoom(room.id)}
                >
                  <ListItemAvatar>
                    <Badge color="primary" badgeContent={room.unread_count} overlap="circular">
                      <Avatar src={roomListAvatarUrl(room, currentUserId) ?? undefined}>
                        {room.type === 'group' ? <GroupIcon /> : (room.title?.[0] ?? '?')}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={room.title ?? t('chat.untitled')}
                    secondary={renderPreview(room, currentUserId, t)}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 400, position: 'relative' }}
          {...composer.dragProps}
        >
          {composer.isDragOver && canWrite && <ChatDropOverlay />}
          {!currentRoom ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('chat.selectRoom')}</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  ...CHAT_TOOLBAR_ROW_SX,
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setInfoOpen(true)}
                >
                  {currentRoom.title ?? t('chat.untitled')}
                </Typography>
                <Tooltip title={currentRoom.is_muted ? t('chat.muteOn') : t('chat.muteOff')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleToggleMute()}
                      disabled={isMuting}
                    >
                      {currentRoom.is_muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <ChatThread
                roomId={currentRoomId}
                showAuthor={currentRoom.type === 'group'}
                canWrite={canWrite}
                currentUserId={currentUserId}
                onEdit={composer.startEdit}
                onDelete={handleDelete}
                onCopyText={handleCopyMessageText}
                onToggleReaction={handleToggleReaction}
                onOpenLessonHistory={handleOpenLessonHistory}
              />

              <ChatComposer composer={composer} />
            </>
          )}
        </Paper>
      </Box>

      <LessonChatHistoryDialog
        meta={lessonHistoryMeta}
        onClose={() => setLessonHistoryMeta(null)}
        onError={setDeepLinkError}
      />

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {currentRoom?.type === 'group' ? t('chat.groupInfo') : t('chat.participantInfo')}
        </DialogTitle>
        <DialogContent dividers>
          {(currentRoom?.members ?? []).map((member) => (
            <Box key={member.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar src={member.user?.avatar_url ?? undefined} sx={{ width: 40, height: 40 }}>
                {member.user?.full_name?.[0] ?? '?'}
              </Avatar>
              <Box>
                <Typography variant="body2">{member.user?.full_name ?? `#${member.user_id}`}</Typography>
                {member.role === 'admin' && (
                  <Typography variant="caption" color="text.secondary">
                    {t('chat.roleAdmin')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chat.selectContact')}</DialogTitle>
        <DialogContent dividers>
          {isLoadingContacts ? (
            <List disablePadding>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))}
            </List>
          ) : contacts.length === 0 ? (
            <Typography color="text.secondary">{t('chat.noContacts')}</Typography>
          ) : (
            <List disablePadding>
              {contacts.map((contact) => (
                <ListItemButton
                  key={contact.user.id}
                  onClick={() => void handleSelectContact(contact)}
                >
                  <ListItemAvatar>
                    <Avatar src={contact.user.avatar_url ?? undefined}>
                      {contact.user.full_name?.[0] ?? '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={contact.user.full_name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}

function filterRoomsBySearch(rooms: ChatRoom[], query: string): ChatRoom[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return rooms

  return rooms.filter((room) => roomMatchesSearch(room, normalized))
}

function roomMatchesSearch(room: ChatRoom, normalizedQuery: string): boolean {
  const parts: string[] = []

  if (room.title) parts.push(room.title)
  if (room.group?.name) parts.push(room.group.name)

  for (const member of room.members ?? []) {
    if (member.user?.full_name) parts.push(member.user.full_name)
  }

  const lastMessage = room.last_message
  if (lastMessage && !lastMessage.is_deleted) {
    if (lastMessage.text) parts.push(lastMessage.text)
    for (const attachment of lastMessage.attachments) {
      if (attachment.name) parts.push(attachment.name)
    }
  }

  const haystack = parts.join(' ').toLocaleLowerCase()
  return haystack.includes(normalizedQuery)
}

function roomListAvatarUrl(room: ChatRoom, currentUserId: number): string | null {
  if (room.type === 'group') {
    return null
  }
  const other = room.members?.find((m) => m.user_id !== currentUserId)
  return other?.user?.avatar_url ?? null
}

/**
 * Превью последнего сообщения в списке комнат: «Вы: текст» для своих и
 * «Имя: текст» для чужих в группе — как в Telegram и VK. В личной переписке
 * сообщение собеседника идёт без префикса: его имя уже стоит заголовком.
 */
function renderPreview(
  room: ChatRoom,
  currentUserId: number,
  t: (key: string) => string
): React.ReactNode {
  const message = room.last_message
  if (!message) return t('chat.noMessages')

  const body = previewBody(message, t)
  const author = previewAuthorPrefix(room, message, currentUserId, t)
  if (!author) return body

  return (
    <>
      <Box component="span" sx={{ color: 'text.primary' }}>
        {author}:{' '}
      </Box>
      {body}
    </>
  )
}

function previewBody(message: ChatMessage, t: (key: string) => string): string {
  if (message.is_deleted) return t('chat.messageDeleted')
  // Плашка истории урока — без текста и вложений: без этой ветки превью
  // списка объявило бы её «Изображением».
  if (isLessonChatWidget(message.meta)) return t('chat.lessonWidget.preview')
  if (message.text) return message.text
  const firstAttachment = message.attachments[0]
  if (firstAttachment?.type === 'sticker') return t('chat.stickerPreview')
  if (firstAttachment?.type === 'file') return firstAttachment.name || t('chat.filePreview')
  return t('chat.imagePreview')
}

/**
 * Имя автора для префикса превью. В last_message списка комнат author приходит
 * загруженным, но на всякий случай есть запасной путь через участников
 * комнаты. Показываем первое слово имени — длинное ФИО съело бы всю строку.
 */
function previewAuthorPrefix(
  room: ChatRoom,
  message: ChatMessage,
  currentUserId: number,
  t: (key: string) => string
): string | null {
  if (message.author_user_id === currentUserId) return t('chat.previewYou')
  if (room.type !== 'group') return null

  const fullName = (
    message.author?.full_name ??
    room.members?.find((m) => m.user_id === message.author_user_id)?.user?.full_name ??
    ''
  ).trim()
  if (!fullName) return null

  return fullName.split(/\s+/)[0] || fullName
}

